import "handsontable/dist/handsontable.full.min.css";
import 'handsontable/styles/ht-theme-main.min.css';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { CommodityManagementApi } from '../../../../../Apis/Apis';
import {
  BudgetModel,
  BudgetRoundModel,
  CommodityModel,
  CounterpartyModel,
  GetCustomerDataResponse,
  GetCustomerSettingsResponse,
  LocationModel,
  MarketPriceProvider,
  Unit,
  YearDataModel
} from '../../../../../Generated/Commodity-Management-Api';
import { ContractDateHelper } from '../../../../../Helpers';
import { useLoadingState } from '../../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { StoneXRow } from '../../../../StoneX';
import { StoneXDivider } from '../../../../StoneX/StoneXDivider';
import StoneXMainPage from '../../../../StoneX/StoneXMainPage/StoneXMainPage';
import { StoneXAutocomplete, StoneXButton, StoneXTextField } from '../../../../StoneXMui';
import EditBudgetsTable, { EditBudgetsTableRef } from '../EditBudgetsTable';
import { BudgetFilters } from '../Types';
import { BudgetRow } from './BudgetTypes';
import { BudgetRoundSelector } from "../../BudgetRoundSelector";

interface UpdateBudgetsProps {
  budgetFilters?: BudgetFilters;  
  onSave: (budgetFilter: BudgetFilters) => void;
  onClose: () => void;
  onCancel?: () => void;
}

export default function UpdateBudgets(props: UpdateBudgetsProps) {
  const { budgetFilters, onCancel, onClose, onSave } = props;
  const { enqueueSnackbar } = useSnackbar();

  const editBudgetTableRef = useRef<EditBudgetsTableRef>(null);

  const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
  const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);
  
  const [budgets, setBudgets] = useState<BudgetRow[]>();
  const [isSaving, setIsSaving] = useState(false);

  const [selectedCommodity, setSelectedCommodity] = useState<CommodityModel | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationModel | null>(null);
  const [selectedCounterparty, setSelectedCounterparty] = useState<CounterpartyModel | null>(null);
  const [selectedBudgetRound, setSelectedBudgetRound] = useState<BudgetRoundModel | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>();
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);

  //init data on load
  const budgetLoadingState = useLoadingState();
  const budgetSaveLoadingState = useLoadingState();

  const originalBudgetsRef = useRef<BudgetModel[]>();

  function loadBudgetsForEdit() {

    if (!budgetFilters) {
      return;
    }

    const { fiscalYear, commodityId, locationId, counterpartyId, budgetRoundId } = budgetFilters!;
    const startMonth = ContractDateHelper.getStartContractDate(fiscalYear!, customerSettings?.fiscalMonthOffset);
    const endMonth = ContractDateHelper.addMonths(startMonth, 11);

    CommodityManagementApi.listBudgets(
      budgetLoadingState.setLoadingState, //budgetLoadingState.setLoadingState,
      undefined,
      startMonth.year,
      startMonth.month,
      undefined,
      endMonth.year,
      endMonth.month,
      undefined,
      undefined,
      commodityId ? [commodityId] : [],
      locationId ? [locationId] : [],
      counterpartyId ? [counterpartyId] : [],
      budgetRoundId ? [budgetRoundId] : [],
      undefined,
      undefined,
      undefined,
      undefined
    ).then(res => {
      originalBudgetsRef.current = res.data.rows ?? undefined;
      editExistingBudgets(res.data.rows ?? undefined);
    }).catch(() => {
      enqueueSnackbar('An error occurred while loading budgets', { variant: 'error' });
    });
  }

  function editExistingBudgets(budgetModels?: BudgetModel[]) {

    if (budgetModels == undefined) {
      setBudgets(undefined);
      return;
    }

    if (!budgetModels.every((x) => x.commodity.commodityId == budgetModels[0].commodity.commodityId)) {
      return [];
    }

    if (!budgetModels.every((x) => x.location?.locationId == budgetModels[0].location?.locationId)) {
      return [];
    }

    if (!budgetModels.every((x) => x.counterparty?.counterpartyId == budgetModels[0].counterparty?.counterpartyId)) {
      return [];
    }

    if (!budgetModels.every((x) => x.unit == budgetModels[0].unit)) {
      return [];
    }

    setSelectedYear(ContractDateHelper.getFiscalYear(budgetModels[0].budgetDate, customerSettings.fiscalMonthOffset!));
    setSelectedLocation(budgetModels[0].location);
    setSelectedCommodity(budgetModels[0].commodity);
    setSelectedCounterparty(budgetModels[0].counterparty);
    setSelectedBudgetRound(budgetModels[0].budgetRoundModel);
    setSelectedUnit(budgetModels[0].unit);

    const budgetRows = budgetModels?.map((budget) => {
      const hasMarkItView = budget.budgetPricingStrategy.components.some((x) => x.marketPriceProvider == MarketPriceProvider.MarketView);
      return {
        ...budget,
        commodityId: budget.commodity.commodityId,
        locationId: budget.location?.locationId,
        counterpartyId: budget.counterparty?.counterpartyId,
        pricingStrategyId: budget.budgetPricingStrategy.budgetPricingStrategyId,
        budgetRoundId: budget.budgetRoundModel?.budgetRoundId,
        hasContractDate: hasMarkItView,
      } as BudgetRow;
    });

    setBudgets(budgetRows);
  }

  function reset() {
    editExistingBudgets(originalBudgetsRef.current);
  }

  async function updateBudgets() {
    const isValid = await editBudgetTableRef.current!.isValid();
    if (!isValid) {
      enqueueSnackbar('Please fill in all required fields.', { variant: 'error' });
      return;
    }

    const budgets = editBudgetTableRef.current!.getBudgets();

    setIsSaving(true);
    CommodityManagementApi.updateBudgets(undefined, budgets)
      .then(async (res) => {

        const budgetFilter = {
          fiscalYear: selectedYear ?? undefined,
          commodityId: selectedCommodity?.commodityId,
          locationId: selectedLocation?.locationId,
          counterpartyId: selectedCounterparty?.counterpartyId,
          budgetRoundId: selectedBudgetRound?.budgetRoundId,
        };

        await onSave(budgetFilter);

        setIsSaving(false);
        enqueueSnackbar('Budgets were successfully updated', {variant: 'success'});
      })
      .catch(err => {
        enqueueSnackbar('An error occurred while saving the budgets', {variant: 'error'});
        setIsSaving(false);
      });
  };

  useEffect(loadBudgetsForEdit, []);

  return (
    <StoneXMainPage isLoading={budgetLoadingState.isLoading()} >
      <StoneXRow>
        <StoneXAutocomplete
          options={customerData.commodities ?? []}
          getOptionLabel={(q) => q.commodityName}
          value={selectedCommodity}
          onChange={(e: any, commodity: CommodityModel | null) => setSelectedCommodity(commodity)}
          disabled
          isOptionEqualToValue={(a, b) => a.commodityId == b.commodityId}
          label="Commodity"
        />
  
        {customerSettings.budgetsByLocation && (
          <StoneXAutocomplete
            options={customerData.budgetingLocations ?? []}
            getOptionLabel={(q) => q.locationName}
            value={selectedLocation}
            onChange={(e: any, location: LocationModel | null) => setSelectedLocation(location)}
            disabled
            label="Location"
          />
        )}
  
        {customerSettings.budgetsByCounterparty && (
          <StoneXAutocomplete
            options={customerData.counterparties ?? []}
            getOptionLabel={(q) => q.counterpartyName!}
            value={selectedCounterparty}
            onChange={(e: any, counterparty: CounterpartyModel | null) => setSelectedCounterparty(counterparty)}
            disabled
            label="Counterparty"
          />
        )}
  
        <StoneXAutocomplete
          value={selectedUnit}
          options={customerData.units ?? []}
          getOptionLabel={(unit) => unit}
          onChange={(e: any, unit: Unit | null) => setSelectedUnit(unit)}
          disabled
          label="Unit"
        />

        <StoneXTextField
          value={selectedYear}
          type='number'
          disabled
          onChange={(e: any) => setSelectedYear(e)}
          label="Year"
        />
        
        {customerSettings.usesBudgetRounds && (
          <BudgetRoundSelector 
            value={selectedBudgetRound}
            disabled
            options={customerData.budgetRounds ?? []}
          />
        )}
      </StoneXRow>

      <StoneXDivider />

      <div style={{display: 'flex', flexDirection: 'column', flexGrow: 1}}>
        {budgets && <EditBudgetsTable ref={editBudgetTableRef} isSaving={isSaving} budgets={budgets} displayPrecision={selectedCommodity?.displayPrecision} />}
      </div>
      
      <StoneXRow>
        <div className="pull-right nested-row-item">
          <StoneXButton variant='outlined' onClick={onCancel} disabled={isSaving}>Cancel</StoneXButton>
          <StoneXButton variant='outlined' onClick={reset} disabled={isSaving}>Reset</StoneXButton>
          <StoneXButton onClick={updateBudgets} disabled={isSaving} isWorking={isSaving}>Save</StoneXButton>
        </div>
      </StoneXRow>

    </StoneXMainPage>
  );
  
}